import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Zap, Scale, BarChart3, RefreshCw, Route, Calendar, Fuel, Plug,
  Calculator, ChevronRight, TrendingUp, Headphones, Leaf,
} from 'lucide-react';
import { useSimulatorSettings } from '../../hooks/useCommerce';

const fmt = (n: number) => new Intl.NumberFormat('fr-FR').format(Math.round(Math.max(0, n)));
const num = (v: unknown, fallback: number) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const DEFAULT_BULLETS = [
  'Comparez avec une moto thermique',
  'Estimez vos gains mensuels',
  'Identifiez votre retour sur investissement',
];
const BULLET_ICONS = [Scale, BarChart3, RefreshCw];

function CtaWrap({ to, className, children }: { to: string; className: string; children: React.ReactNode }) {
  if (/^https?:\/\//.test(to)) return <a href={to} className={className}>{children}</a>;
  return <Link to={to} className={className}>{children}</Link>;
}

const Field: React.FC<{ icon: React.ReactNode; label: string; value: number; unit?: string; onChange: (n: number) => void }> = ({
  icon, label, value, unit, onChange,
}) => (
  <div className="flex items-center justify-between gap-3 rounded-2xl bg-white px-4 py-3 shadow-[0_1px_2px_rgba(10,22,40,0.04)]">
    <span className="flex items-center gap-3 text-sm font-semibold text-neutral-700">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-50 text-primary-600">{icon}</span>
      {label}
    </span>
    <span className="flex items-center gap-1 rounded-xl border border-neutral-200 bg-white px-3 py-1.5">
      <input
        type="number"
        inputMode="numeric"
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-[64px] border-0 bg-transparent p-0 text-right text-sm font-bold text-neutral-900 focus:outline-none focus:ring-0"
      />
      {unit && <span className="text-xs font-semibold text-neutral-400">{unit}</span>}
    </span>
  </div>
);

const SavingsSimulator: React.FC = () => {
  const { settings: s } = useSimulatorSettings();
  const weeks = num(s?.weeks_per_month, 4);
  const co2f = num(s?.co2_factor_g, 68);

  const [km, setKm] = useState(80);
  const [days, setDays] = useState(6);
  const [fuel, setFuel] = useState(5000);
  const [rech, setRech] = useState(1000);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (s && !touched) {
      setKm(num(s.default_km, 80));
      setDays(num(s.default_days, 6));
      setFuel(num(s.default_fuel, 5000));
      setRech(num(s.default_recharge, 1000));
    }
  }, [s]); // eslint-disable-line react-hooks/exhaustive-deps

  const change = (setter: (n: number) => void) => (n: number) => { setTouched(true); setter(n); };

  const mois = Math.max(0, fuel - rech) * days * weeks;
  const an = mois * 12;
  const co2 = (km * days * weeks * co2f) / 1000;

  const eyebrow = s?.eyebrow || 'Économies';
  const title = s?.title || 'Simulez vos économies';
  const subtitle = s?.subtitle || 'Découvrez combien vous pouvez économiser en passant à la mobilité électrique.';
  const bullets = (Array.isArray((s as any)?.bullets) && (s as any).bullets.length ? (s as any).bullets : DEFAULT_BULLETS) as string[];
  const image = (s as any)?.image_url || '/images/simulateur-economies.png';
  const resultLabel = s?.result_label || 'Économie estimée';
  const ctaLabel = s?.cta_label || 'Simuler maintenant';
  const ctaUrl = s?.cta_url || '/pay-as-you-go';
  const secLabel = s?.secondary_label || 'Parler à un conseiller';
  const secUrl = s?.secondary_url || '/contact';

  return (
    <section className="bg-gradient-to-b from-white to-primary-50 py-12 md:py-16">
      <div className="container mx-auto px-4 md:px-6">
        <div className="overflow-hidden rounded-[28px] border border-neutral-200 bg-white p-5 shadow-[0_40px_80px_-50px_rgba(10,22,40,0.35)] md:p-8">
          <div className="grid items-center gap-8 md:grid-cols-2 md:gap-10">
            {/* ----- Colonne gauche : pitch + illustration ----- */}
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-primary-600">
                <Zap size={15} className="fill-current" /> {eyebrow}
              </div>
              <h2 className="mt-2 text-4xl font-black leading-[1.05] tracking-tight text-neutral-900 md:text-5xl">{title}</h2>
              <p className="mt-3 max-w-md text-[15px] leading-relaxed text-neutral-500">{subtitle}</p>

              <ul className="mt-6 space-y-3.5">
                {bullets.map((b, i) => {
                  const Icon = BULLET_ICONS[i] || Scale;
                  return (
                    <li key={i} className="flex items-center gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                        <Icon size={19} strokeWidth={2} />
                      </span>
                      <span className="text-[15px] font-semibold text-neutral-800">{b}</span>
                    </li>
                  );
                })}
              </ul>

              {image && (
                <img
                  src={image}
                  alt="Moto électrique Proxy en recharge sur une borne solaire"
                  loading="lazy"
                  className="mt-7 w-full max-w-[440px] select-none"
                />
              )}
            </div>

            {/* ----- Colonne droite : simulateur ----- */}
            <div className="rounded-3xl border border-neutral-200 bg-neutral-50 p-4 md:p-5">
              <div className="space-y-2.5">
                <Field icon={<Route size={17} />} label="Kilométrage quotidien" value={km} unit="km" onChange={change(setKm)} />
                <Field icon={<Calendar size={17} />} label="Jours travaillés / semaine" value={days} onChange={change(setDays)} />
                <Field icon={<Fuel size={17} />} label="Dépense carburant / jour" value={fuel} unit="FCFA" onChange={change(setFuel)} />
                <Field icon={<Plug size={17} />} label="Coût recharge estimé / jour" value={rech} unit="FCFA" onChange={change(setRech)} />
              </div>

              <div className="mt-3 rounded-2xl border border-primary-100 bg-primary-50/70 p-4 text-center">
                <span className="text-xs font-extrabold uppercase tracking-[0.14em] text-primary-600">{resultLabel}</span>
                <div className="mt-1 text-[40px] font-black leading-none tracking-tight text-primary-700 md:text-[46px]">
                  {fmt(mois)} <span className="text-xl font-extrabold text-primary-600">FCFA</span> <span className="text-lg font-medium text-primary-600/80">/ mois</span>
                </div>
                <p className="mt-1.5 flex items-center justify-center gap-1.5 text-sm text-neutral-600">
                  <TrendingUp size={15} className="text-primary-600" /> Soit <b className="text-neutral-900">{fmt(an)}</b> FCFA / an
                </p>
                <p className="mt-1.5 flex items-center justify-center gap-1.5 text-xs text-neutral-400">
                  <Leaf size={12} className="text-primary-500" /> ≈ {fmt(co2)} kg CO₂ évités / mois
                </p>
              </div>

              <CtaWrap
                to={ctaUrl}
                className="mt-3 flex items-center justify-center gap-2 rounded-2xl bg-primary-600 px-5 py-4 text-base font-extrabold text-white transition-all hover:-translate-y-0.5 hover:bg-primary-700"
              >
                <Calculator size={19} /> {ctaLabel} <ChevronRight size={18} className="ml-0.5" />
              </CtaWrap>
              <CtaWrap
                to={secUrl}
                className="mt-3 flex items-center justify-center gap-1.5 text-sm font-bold text-primary-600 underline-offset-4 hover:underline"
              >
                <Headphones size={15} /> {secLabel}
              </CtaWrap>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SavingsSimulator;
